import React, { useCallback, forwardRef, useRef } from "react";

import { Form as FormikForm, useFormikContext } from "formik";

import ScrollToErrorField from "./ScrollToErrorField";
import {
  getFieldsWithServerError,
  scrollToError,
} from "./ScrollToErrorField/utils";

interface FormWrapperProps {
  className?: string;
  formProps?: React.ComponentProps<"form">;
  children: React.ReactNode;
  scrollToErrorField: boolean;
}

const FormWrapper = forwardRef<HTMLFormElement, FormWrapperProps>(
  ({ className, formProps, children, scrollToErrorField }, ref) => {
    const { validateForm, setErrors, setTouched, submitForm, ...formikBag } =
      useFormikContext();

    const { dirty: isFormDirty, isSubmitting, status } = formikBag;

    const formRefForScrollToErrorField = useRef<HTMLFormElement>(null);
    const formRef =
      (ref as React.RefObject<HTMLFormElement | null>) ||
      formRefForScrollToErrorField;

    const handleKeyDown = useCallback(
      async (event: React.KeyboardEvent<HTMLFormElement>) => {
        const target = event.target as HTMLElement & { editor?: unknown };
        const isEventFromEditorOrTextarea =
          target.tagName === "TEXTAREA" || target.editor;

        if (event.key !== "Enter") return;
        if (isEventFromEditorOrTextarea && !event.metaKey) return;

        event.preventDefault();

        if (event.shiftKey) return;
        if (!isFormDirty || isSubmitting) return;

        try {
          const errors = await validateForm();
          const fieldStatuses = getFieldsWithServerError(status);

          if (
            Object.keys(errors).length > 0 ||
            Object.keys(fieldStatuses).length > 0
          ) {
            setErrors(errors);
            setTouched({ ...errors, ...status });
            if (scrollToErrorField) {
              scrollToError(formRef, errors as Record<string, unknown>, status);
            }
          } else {
            submitForm();
          }
        } catch (error) {
          console.error(
            "An unhandled error was caught from validateForm()",
            error
          );
        }
      },
      [
        validateForm,
        setErrors,
        setTouched,
        isFormDirty,
        isSubmitting,
        submitForm,
        status,
        scrollToErrorField,
        formRef,
      ]
    );

    return (
      <FormikForm
        {...{ className }}
        noValidate
        ref={formRef}
        onKeyDown={handleKeyDown}
        {...formProps}
      >
        {scrollToErrorField && <ScrollToErrorField {...{ formRef }} />}
        {children}
      </FormikForm>
    );
  }
);

FormWrapper.displayName = "FormWrapper";

export default FormWrapper;
